import React, { useEffect, useState, useCallback } from "react";
import get from "lodash/get";
import { Button, Modal } from "react-bootstrap";
import Select from "react-select";
import { toast } from "react-toastify";
import textUtils from "../../common/utilities/TextUtils";
import { GENERIC_RESPONSE_PARAMETER, ADD_UPDATE, getApiPath } from "../../../api/ApiConst";
import apiCaller from "../../../api/ApiCaller";
import { TABLE_ATTRIBUTE_TYPES } from "../../../settings/constants/types";
import { getDataTypes, DataTypeConfigView } from "../../../utils/datatypes/DataTypeConfig";

// Helper function to find nested attributes
const findNestedAttributes = (attributes, path) => {
    if (!path.length) return attributes;
    const [current, ...rest] = path;
    const currentAttr = attributes[current];
    return rest.length ? findNestedAttributes(currentAttr.attributes, rest) : currentAttr.attributes;
};

// Helper function to get attribute path names
const getAttributePathNames = (attributes, path) => {
    if (!path.length) return [];
    const [current, ...rest] = path;
    const currentAttr = attributes[current];
    return [
        { index: current, name: currentAttr.name || `Attribute ${current + 1}` },
        ...getAttributePathNames(currentAttr.attributes, rest)
    ];
};

// Helper function to update nested attributes
const updateNestedAttributes = (attributes, path, newAttributes) => {
    if (!path.length) return newAttributes;
    const [current, ...rest] = path;
    const newArray = [...attributes];
    if (rest.length) {
        newArray[current] = {
            ...newArray[current],
            attributes: updateNestedAttributes(newArray[current].attributes, rest, newAttributes)
        };
    } else {
        newArray[current] = {
            ...newArray[current],
            attributes: newAttributes
        };
    }
    return newArray;
};

// Helper function to validate attribute name
const isValidAttributeName = (name) => {
    return name && name.trim().length > 0;
};

const BreadcrumbNav = ({ pathNames, onNavigate }) => {
    return (
        <nav aria-label="breadcrumb">
            <ol className="breadcrumb mb-3">
                <li className="breadcrumb-item">
                    <Button
                        variant="link"
                        className="p-0"
                        onClick={() => onNavigate([])}
                    >
                        Root
                    </Button>
                </li>
                {pathNames.map((item, index) => (
                    <li key={index} className="breadcrumb-item">
                        <Button
                            variant="link"
                            className="p-0"
                            onClick={() => onNavigate(pathNames.slice(0, index + 1).map(p => p.index))}
                        >
                            {item.name}
                        </Button>
                    </li>
                ))}
            </ol>
        </nav>
    );
};

const AttributeEditor = ({ attributes = [], onChange, currentPath = [], onPathChange }) => {
    const [currentAttributes, setCurrentAttributes] = useState([]);
    const [pathNames, setPathNames] = useState([]);
    const [typeOptions, setTypeOptions] = useState([]);
    const [isLoadingTypes, setIsLoadingTypes] = useState(false);

    // Load current attributes and path names
    useEffect(() => {
        const attrs = findNestedAttributes(attributes || [], currentPath) || [];
        const names = getAttributePathNames(attributes || [], currentPath);
        setCurrentAttributes(attrs);
        setPathNames(names);
    }, [attributes, currentPath]);

    // Load type options from API
    useEffect(() => {
        loadTypeOptions();
    }, []);

    const loadTypeOptions = async () => {
        setIsLoadingTypes(true);
        try {
            const options = await getDataTypes();
            setTypeOptions(options);
        } catch (error) {
            console.error('Error loading type options:', error);
            setTypeOptions([]);
        } finally {
            setIsLoadingTypes(false);
        }
    };

    const handleAddAttribute = () => {
        const newAttributes = [...currentAttributes, {
            name: "",
            type: "",
            description: "",
            listAttribute: false,
            attributes: []
        }];
        onChange(updateNestedAttributes(attributes || [], currentPath, newAttributes));
    };

    const handleRemoveAttribute = (index) => {
        const newAttributes = [...currentAttributes];
        newAttributes.splice(index, 1);
        onChange(updateNestedAttributes(attributes || [], currentPath, newAttributes));
    };

    const handleAttributeChange = (index, field, value) => {
        const newAttributes = [...currentAttributes];
        const updatedAttribute = { ...newAttributes[index], [field]: value };
        
        // If type is changing to OBJECT, initialize config structure
        if (field === 'type' && value === 'OBJECT') {
            updatedAttribute.config = {
                ...updatedAttribute.config,
                attributes: updatedAttribute.config?.attributes || updatedAttribute.attributes || []
            };
            // Remove the direct attributes field to prevent duplication
            delete updatedAttribute.attributes;
        }
        
        // If type is changing from OBJECT to something else, move attributes back to direct field
        if (field === 'type' && newAttributes[index].type === 'OBJECT' && value !== 'OBJECT') {
            updatedAttribute.attributes = updatedAttribute.config?.attributes || [];
            delete updatedAttribute.config;
        }
        
        newAttributes[index] = updatedAttribute;
        onChange(updateNestedAttributes(attributes || [], currentPath, newAttributes));
    };

    const handleNavigateToNested = (index) => {
        onPathChange([...currentPath, index]);
    };

    const canAddAttribute = Array.isArray(currentAttributes) &&
        (currentAttributes.length === 0 || currentAttributes.every(attr => attr && isValidAttributeName(attr.name)));

    return (
        <div>
            {currentPath.length > 0 && (
                <BreadcrumbNav
                    pathNames={pathNames}
                    onNavigate={onPathChange}
                />
            )}
            <div className="mb-3">
                {Array.isArray(currentAttributes) && currentAttributes.map((attr, index) => (
                    <div key={index} className="mb-3 p-2 border rounded">
                        <div className="row mb-2">
                            <div className="col-md-4">
                                <input
                                    type="text"
                                    className={`form-control ${!isValidAttributeName(attr?.name) ? 'is-invalid' : ''}`}
                                    placeholder="Attribute Name"
                                    value={attr?.name || ""}
                                    onChange={(e) => handleAttributeChange(index, "name", e.target.value)}
                                />
                                {!isValidAttributeName(attr?.name) && (
                                    <div className="invalid-feedback">
                                        Attribute name is required
                                    </div>
                                )}
                            </div>
                            <div className="col-md-4">
                                <Select
                                    options={typeOptions}
                                    value={{ label: attr?.type || "", value: attr?.type || "" }}
                                    onChange={(e) => handleAttributeChange(index, "type", e.value)}
                                    className="react-select-container"
                                    isLoading={isLoadingTypes}
                                    placeholder={isLoadingTypes ? "Loading types..." : "Select Type"}
                                />
                            </div>
                            <div className="col-md-2">
                                <Button
                                    variant="danger"
                                    size="sm"
                                    onClick={() => handleRemoveAttribute(index)}
                                >
                                    Remove
                                </Button>
                            </div>
                        </div>
                        <div className="row mb-2">
                            <div className="col-md-8">
                                <input
                                    type="text"
                                    className="form-control"
                                    placeholder="Description (optional)"
                                    value={attr?.description || ""}
                                    onChange={(e) => handleAttributeChange(index, "description", e.target.value)}
                                />
                            </div>
                        </div>
                        <div className="row mb-2">
                            <div className="col-md-4">
                                <input
                                    type="checkbox"
                                    name={`listAttribute_${index}`}
                                    id={`listAttribute_${index}`}
                                    checked={attr?.listAttribute || false}
                                    onChange={(e) => handleAttributeChange(index, "listAttribute", e.target.checked)}
                                />
                                <label className='ms-2'>List Attribute</label>
                            </div>
                        </div>
                        {(attr?.type === "OBJECT") && (
                            <div className="mt-2">
                                <Button
                                    variant="outline-primary"
                                    size="sm"
                                    onClick={() => handleNavigateToNested(index)}
                                    disabled={!isValidAttributeName(attr?.name)}
                                >
                                    Edit Nested Attributes ({(attr?.config?.attributes || attr?.attributes || []).length})
                                </Button>
                            </div>
                        )}
                    </div>
                ))}
                <Button
                    variant="outline-primary"
                    size="sm"
                    onClick={handleAddAttribute}
                    disabled={!canAddAttribute}
                >
                    Add Attribute
                </Button>
            </div>
        </div>
    );
};

const INITIAL_STATE = {
    requestId: "",
    title: "",
    name: "",
    description: "",
    type: "",
    required: false,
    attributes: [],
    listAttribute: false,
};

const ResponseParameterAddEdit = ({
    isOpen = false,
    row = null,
    requestId,
    onSuccessCallBack,
    onClose,
}) => {
    const [parameterTypeKeyValue, setParameterTypeKeyValue] = useState("");
    const [currentPath, setCurrentPath] = useState([]);
    const [typeOptions, setTypeOptions] = useState([]);
    const [isLoadingTypes, setIsLoadingTypes] = useState(false);
    const [configData, setConfigData] = useState({});
    const [parameterObj, setParameterObj] = useState({ ...INITIAL_STATE });

    // Load type options from API
    useEffect(() => {
        loadTypeOptions();
    }, []);

    const loadTypeOptions = async () => {
        setIsLoadingTypes(true);
        try {
            const options = await getDataTypes();
            setTypeOptions(options);
        } catch (error) {
            console.error('Error loading type options:', error);
            setTypeOptions([]);
        } finally {
            setIsLoadingTypes(false);
        }
    };

    useEffect(() => {
        if (isOpen) {
            const rowId = get(row, "id");
            if (rowId) {
                const obj = {
                    id: rowId,
                    requestId,
                    title: get(row, "title", ""),
                    name: get(row, "name", ""),
                    description: get(row, "description", ""),
                    type: get(row, "type", ""),
                    required: get(row, "required", false),
                    attributes: get(row, "attributes", []),
                    listAttribute: get(row, "listAttribute", false),
                };
                setParameterObj(obj);
                
                if (get(row, "type", "") !== "") {
                    setParameterTypeKeyValue({
                        label: get(row, "type", ""),
                        value: get(row, "type", "")
                    });
                    // Load existing config data if available
                    if (row.config) {
                        setConfigData(row.config);
                    }
                } else {
                    setParameterTypeKeyValue("");
                    setConfigData({});
                }
            } else {
                setParameterObj({ ...INITIAL_STATE, requestId });
                setParameterTypeKeyValue("");
                setConfigData({});
            }
        }
        setCurrentPath([]);
    }, [isOpen, row, requestId]);

    const toggle = () => {
        onClose();
    };

    const handleTextChange = (e, type) => {
        if (type === "checkbox") {
            setParameterObj(prev => ({ ...prev, [e.target.id]: e.target.checked }));
        } else if (type === "text") {
            setParameterObj(prev => ({ ...prev, [e.target.id]: e.target.value }));
        } else if (type === "select") {
            setParameterTypeKeyValue(e);
            setParameterObj(prev => ({ ...prev, type: e.value }));
            // Clear config data when type changes
            setConfigData({});
        }
    };

    const handleConfigChange = useCallback((fieldName, value) => {
        setConfigData(prev => {
            const newConfig = {
                ...prev,
                [fieldName]: value
            };
            console.log('Config changed:', fieldName, value, newConfig);
            return newConfig;
        });
    }, []);

    const handleCreateParameter = () => {
        let requestObj = parameterObj;
        if (
            textUtils.isEmpty(requestObj.name, "Name", true) ||
            textUtils.isEmpty(requestObj.description, "Description", true) ||
            textUtils.isEmpty(requestObj.type, "Type", true)
        ) {
            return false;
        }

        // Validate attributes if type is OBJECT 
        if ((parameterObj.type === TABLE_ATTRIBUTE_TYPES.OBJECT) &&
            (!parameterObj.attributes || parameterObj.attributes.length === 0)) {
            toast.error("Please add at least one attribute for the object type");
            return false;
        }

        // Validate all attribute names and nested objects
        const validateAttributes = (attrs) => {
            if (!Array.isArray(attrs)) return true;
            
            return attrs.every(attr => {
                if (!attr) return true;

                // Check if attribute name is valid
                if (!isValidAttributeName(attr.name)) {
                    toast.error(`Attribute "${attr.name || 'Unnamed'}" must have a valid name`);
                    return false;
                }

                // Check if nested objects have at least one inner attribute
                if ((attr.type === TABLE_ATTRIBUTE_TYPES.OBJECT)) {
                    if (!attr.attributes || attr.attributes.length === 0) {
                        toast.error(`Nested object "${attr.name}" must have at least one inner attribute`);
                        return false;
                    }
                    // Recursively validate nested attributes
                    return validateAttributes(attr.attributes);
                }

                return true;
            });
        };

        // Always validate attributes if they exist, regardless of the main type
        if (parameterObj.attributes && parameterObj.attributes.length > 0) {
            if (!validateAttributes(parameterObj.attributes)) {
                return false;
            }
        }

        // Remove attributes key if type is not OBJECT
        if (parameterObj.type !== TABLE_ATTRIBUTE_TYPES.OBJECT) {
            delete requestObj.attributes;
        } else {
            // Clean nested attributes - remove attributes key from inner elements that don't need it
            const cleanAttributes = (attrs) => {
                if (!Array.isArray(attrs)) return attrs;
                return attrs.map(attr => {
                    if (!attr) return attr;
                    const cleanedAttr = { ...attr };
                    if (attr.type !== TABLE_ATTRIBUTE_TYPES.OBJECT) {
                        delete cleanedAttr.attributes;
                    } else if (attr.attributes) {
                        cleanedAttr.attributes = cleanAttributes(attr.attributes);
                    }
                    return cleanedAttr;
                });
            };
            requestObj.attributes = cleanAttributes(requestObj.attributes);
        }

        // Add config data to the request object
        if (Object.keys(configData).length > 0) {
            requestObj.config = configData;
        }

        let url = getApiPath(GENERIC_RESPONSE_PARAMETER, ADD_UPDATE);
        apiCaller.callPostApi(
            url,
            requestObj,
            {},
            (response) => {
                toast.success(
                    `Parameter ${parameterObj?.id ? "Update" : "Created"} Successfully`
                );
                onSuccessCallBack();
                toggle();
            },
            (error) => {
                // toast.error(error.response.data.message);
            }
        );
    };

    return (
        <>
            <Modal size="lg" show={isOpen} onHide={toggle}>
                <Modal.Header closeButton>
                    <Modal.Title>{`${parameterObj?.id ? "Edit" : "Add"
                        } Response Parameter`}</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <div className="form-group row mb-3">
                        <div className="col-lg-12">
                            <label>Title</label>
                            <input
                                type="text"
                                className="form-control input-default"
                                id="title"
                                placeholder="Title"
                                onChange={(e) => handleTextChange(e, e.target.type)}
                                value={parameterObj.title}
                            />
                        </div>
                    </div>
                    <div className="form-group row mb-3">
                        <div className="col-lg-12">
                            <label>Name *</label>
                            <input
                                type="text"
                                className="form-control input-default"
                                id="name"
                                placeholder="Name"
                                onChange={(e) => handleTextChange(e, e.target.type)}
                                value={parameterObj.name}
                            />
                        </div>
                    </div>
                    <div className="form-group row mb-3">
                        <div className="col-lg-12">
                            <label>Description *</label>
                            <input
                                type="text"
                                className="form-control input-default"
                                id="description"
                                placeholder="Description"
                                onChange={(e) => handleTextChange(e, e.target.type)}
                                value={parameterObj.description}
                            />
                        </div>
                    </div>
                    <div className="form-group row mb-3">
                        <div className="col-lg-12">
                            <label>Type *</label>
                            <Select
                                options={typeOptions}
                                value={parameterTypeKeyValue}
                                id="type"
                                onChange={(e) => handleTextChange(e, "select")}
                                className="react-select-container"
                                isLoading={isLoadingTypes}
                                placeholder={isLoadingTypes ? "Loading types..." : "Select Type"}
                            />
                        </div>
                    </div>
                    <div className="form-group row mb-3">
                        <div className="col-lg-12 text-left">
                            <label className=''>List Attribute</label>
                            <input
                                type="checkbox"
                                name="listAttribute"
                                id="listAttribute"
                                checked={parameterObj.listAttribute}
                                onChange={(e) => handleTextChange(e, e.target.type)}
                                style={{
                                    display: 'block',
                                    width:'max-content',
                                    marginLeft:'5px'
                                }}
                            />
                        </div>
                    </div>
                    {(parameterObj.type === TABLE_ATTRIBUTE_TYPES.OBJECT) && (
                        <div className="form-group row mb-3">
                            <div className="col-lg-12">
                                <label>Attributes *</label>
                                <AttributeEditor
                                    attributes={parameterObj.attributes || []}
                                    onChange={(attributes) => setParameterObj(prev => ({ ...prev, attributes }))}
                                    currentPath={currentPath}
                                    onPathChange={setCurrentPath}
                                />
                            </div>
                        </div>
                    )}
                    <div className="form-group row mb-3">
                        <div className="col-lg-12">
                            <div className="row">
                                <div className="col-lg-6">
                                    <label className="">Required</label>
                                    <input
                                        type="checkbox"
                                        id="required"
                                        checked={parameterObj.required}
                                        onChange={(e) => handleTextChange(e, e.target.type)}
                                        style={{
                                            display: 'block',
                                            width:'max-content',
                                            marginLeft:'5px'
                                        }}
                                    />
                                </div>
                            </div>
                        </div>
                    </div>
                    
                    {/* Data Type Configuration */}
                    {parameterTypeKeyValue && (
                        <DataTypeConfigView
                            key={`config-${parameterTypeKeyValue.value}`}
                            selectedDataType={parameterTypeKeyValue}
                            configData={configData}
                            onConfigChange={handleConfigChange}
                        />
                    )}
                </Modal.Body>
                <Modal.Footer>
                    <Button variant="primary" onClick={handleCreateParameter}>
                        Save Changes
                    </Button>
                </Modal.Footer>
            </Modal>
        </>
    );
};

export default ResponseParameterAddEdit;
